import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';

interface Tenant {
  legal_name: string;
  uuid: string;
}

interface Registry {
  id: string;
  name: string;
}

interface Repository {
  name: string;
  registry_name: string;
}

interface Image {
  digest: string;
  pushed_at: string;
  size_bytes: number;
  tags: string[];
}

export function ContainerRegistry() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [registries, setRegistries] = useState<Registry[]>([]);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [images, setImages] = useState<Image[]>([]);
  
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedRegistry, setSelectedRegistry] = useState<Registry | null>(null);
  const [selectedRepository, setSelectedRepository] = useState<Repository | null>(null);
  
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isLoadingRegistries, setIsLoadingRegistries] = useState(false);
  const [isLoadingRepositories, setIsLoadingRepositories] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  const { addToast } = useToast();

  // Carregar tenants ao montar o componente
  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setIsLoadingTenants(true);
      const response = await api.get('api/mgc/tenants');
      setTenants(response.data.data);
      
      if (response.data.data.length === 0) {
        addToast({
          type: 'info',
          title: 'Nenhum tenant encontrado',
          description: 'Não foram encontrados tenants disponíveis.'
        });
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar tenants',
        description: error.message
      });
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const handleTenantSelect = async (tenant: Tenant) => {
    try {
      // Se selecionou o mesmo tenant, não faz nada
      if (selectedTenant?.uuid === tenant.uuid) return;

      // Limpa todos os estados subsequentes
      setSelectedRegistry(null);
      setSelectedRepository(null);
      setRegistries([]);
      setRepositories([]);
      setImages([]);
      
      setSelectedTenant(tenant);
      
      // Setar o tenant selecionado
      await api.put(`/api/mgc/tenants/${tenant.uuid}`);
      
      // Carregar registries após setar o tenant
      setIsLoadingRegistries(true);
      const response = await api.get('api/mgc/registries');
      setRegistries(response.data.data);

      if (response.data.data.length === 0) {
        addToast({
          type: 'info',
          title: 'Nenhum registry encontrado',
          description: 'Não foram encontrados registries disponíveis para este tenant.'
        });
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao selecionar tenant',
        description: error.message
      });
    } finally {
      setIsLoadingRegistries(false);
    }
  };

  const handleRegistrySelect = async (registry: Registry) => {
    try {
      // Se selecionou o mesmo registry, não faz nada
      if (selectedRegistry?.id === registry.id) return;

      // Limpa todos os estados subsequentes
      setSelectedRepository(null);
      setRepositories([]);
      setImages([]);
      
      setSelectedRegistry(registry);
      
      setIsLoadingRepositories(true);
      const response = await api.get(`/api/mgc/repositories/${registry.id}`);
      setRepositories(response.data.data);

      if (response.data.data.length === 0) {
        addToast({
          type: 'info',
          title: 'Nenhum repositório encontrado',
          description: 'Não foram encontrados repositórios disponíveis para este registry.'
        });
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar repositórios',
        description: error.message
      });
    } finally {
      setIsLoadingRepositories(false);
    }
  };

  const handleRepositorySelect = async (repository: Repository) => {
    try {
      // Se selecionou o mesmo repositório, não faz nada
      if (selectedRepository?.name === repository.name) return;

      // Limpa as imagens antes de carregar novas
      setImages([]);
      
      setSelectedRepository(repository);
      setIsLoadingImages(true);
      
      const response = await api.post(`/api/mgc/images/${selectedRegistry?.id}`, {
        repository: repository.name
      });
      setImages(response.data.data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar imagens',
        description: error.message
      });
    } finally {
      setIsLoadingImages(false);
    }
  };

  return (
    <div className="container mx-auto px-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold text-gray-900">Container Registry</h1>
      </div>

      {/* Filtros em linha */}
      <div className="bg-white rounded-lg shadow-lg mb-4">
        <div className="p-4">
          <div className="flex gap-4">
            {/* Tenant Select */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">1. Selecione o Tenant</label>
              <div className="relative">
                <select
                  value={selectedTenant?.uuid || ''}
                  onChange={(e) => {
                    const tenant = tenants.find(t => t.uuid === e.target.value);
                    if (tenant) handleTenantSelect(tenant);
                  }}
                  className={`block w-full rounded-md border bg-white py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 appearance-none ${
                    isLoadingTenants 
                      ? 'border-gray-300 cursor-wait' 
                      : tenants.length === 0 
                        ? 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  disabled={isLoadingTenants}
                >
                  <option value="">
                    {isLoadingTenants 
                      ? 'Carregando tenants...'
                      : tenants.length === 0
                        ? 'Nenhum tenant encontrado'
                        : 'Selecione um tenant'
                    }
                  </option>
                  {tenants.map((tenant) => (
                    <option key={tenant.uuid} value={tenant.uuid}>
                      {tenant.legal_name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  {isLoadingTenants ? (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </div>
            </div>

            {/* Registry Select */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">2. Selecione o Registry</label>
              <div className="relative">
                <select
                  value={selectedRegistry?.id || ''}
                  onChange={(e) => {
                    const registry = registries.find(r => r.id === e.target.value);
                    if (registry) handleRegistrySelect(registry);
                  }}
                  className={`block w-full rounded-md border bg-white py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 appearance-none ${
                    !selectedTenant
                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : isLoadingRegistries
                        ? 'border-gray-300 cursor-wait'
                        : registries.length === 0
                          ? 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  disabled={!selectedTenant || isLoadingRegistries}
                >
                  <option value="">
                    {!selectedTenant
                      ? 'Selecione um tenant primeiro'
                      : isLoadingRegistries
                        ? 'Carregando registries...'
                        : registries.length === 0
                          ? 'Nenhum registry encontrado'
                          : 'Selecione um registry'
                    }
                  </option>
                  {registries.map((registry) => (
                    <option key={registry.id} value={registry.id}>
                      {registry.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  {isLoadingRegistries ? (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </div>
            </div>

            {/* Repository Select */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">3. Selecione o Repositório</label>
              <div className="relative">
                <select
                  value={selectedRepository?.name || ''}
                  onChange={(e) => {
                    const repository = repositories.find(r => r.name === e.target.value);
                    if (repository) handleRepositorySelect(repository);
                  }}
                  className={`block w-full rounded-md border bg-white py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 appearance-none ${
                    !selectedRegistry
                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : isLoadingRepositories
                        ? 'border-gray-300 cursor-wait'
                        : repositories.length === 0
                          ? 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  disabled={!selectedRegistry || isLoadingRepositories}
                >
                  <option value="">
                    {!selectedRegistry
                      ? 'Selecione um registry primeiro'
                      : isLoadingRepositories
                        ? 'Carregando repositórios...'
                        : repositories.length === 0
                          ? 'Nenhum repositório encontrado'
                          : 'Selecione um repositório'
                    }
                  </option>
                  {repositories.map((repository) => (
                    <option key={repository.name} value={repository.name}>
                      {repository.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  {isLoadingRepositories ? (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Images */}
      {selectedRepository && (
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-800">4. Imagens Disponíveis</h2>
          </div>
          <div className="p-4">
            {isLoadingImages ? (
              <div className="text-center py-2 text-sm">Carregando imagens...</div>
            ) : (
              <div className="space-y-3">
                {images.map((image) => (
                  <div
                    key={image.digest}
                    className="border rounded-lg p-3"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Digest</p>
                        <p className="font-mono text-xs">{image.digest}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Pushed At</p>
                        <p className="text-sm">
                          {image.pushed_at ? new Date(image.pushed_at).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            timeZone: 'America/Sao_Paulo'
                          }) : 'Data não disponível'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Size</p>
                        <p className="text-sm">{(image.size_bytes / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Tags</p>
                        <div className="flex flex-wrap gap-1">
                          {image.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-gray-100 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {images.length === 0 && (
                  <div className="text-center py-2 text-sm text-gray-500">
                    Nenhuma imagem encontrada
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 